import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useGoogleLogin } from "@react-oauth/google";
import { GoogleDriveIcon } from "../Icons";
import { useAuth } from "../../../features/auth/AuthContext";
import {
  resetsubmitgoogleToken,
  submitGoogleToken,
} from "../../../state/actions/submitGoogleTokenAction";
import { getGoogleTokenAction } from "../../../state/actions/getGoogleTokenAction";

const GoogleDriveUpload = ({
  setShowUploadPop,
  onFilesDownloaded,
  setIsLoading,
  setShowUploadFileModal,
  mobileUpload = false,
  Icon,
  textClassName = "",
}) => {
  const googleAccessToken = useSelector((state) => state.getGoogleAccessToken);
  const [accessToken, setAccessToken] = useState(
    googleAccessToken?.googleToken?.google_token?.token,
  );
  const [pickerInited, setPickerInited] = useState(false);
  const [statusCode, setStatusCode] = useState(null);
  const fileInputRef = useRef(null);
  const scrollListenerRef = useRef(null); // 👈 holds reference to active scroll listener

  const dispatch = useDispatch();
  const { token } = useAuth();
  const googleToken = useSelector((state) => state.submitGoogleToken);

  // Attach a scroll listener that snaps the page back to savedScrollY
  // whenever the Google Picker iframe steals focus and causes a scroll
  const attachScrollGuard = (savedScrollY) => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current);
    }
    const handler = () => {
      window.scrollTo({ top: savedScrollY, left: 0 });
    };
    scrollListenerRef.current = handler;
    window.addEventListener("scroll", handler);
  };

  const detachScrollGuard = () => {
    if (scrollListenerRef.current) {
      window.removeEventListener("scroll", scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
  };

  const handleTokenExpiration = async () => {
    const currentTime = Date.now();
    const expiryTime = new Date(
      googleAccessToken?.googleToken?.google_token?.expiry,
    ).getTime();

    if (currentTime > expiryTime) {
      const refreshToken =
        googleAccessToken?.googleToken?.google_token?.refresh_token;

      if (!refreshToken) return;

      try {
        const response = await fetch("https://oauth2.googleapis.com/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
            client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
            refresh_token: refreshToken,
            grant_type: "refresh_token",
          }),
        });

        const data = await response.json();
        if (data.error) return;

        const newAccessToken = data.access_token;
        const newExpiryTime = Date.now() + data.expires_in * 1000;

        localStorage.setItem("access_token", newAccessToken);
        localStorage.setItem("token_expiry", newExpiryTime);
        localStorage.setItem(
          "refresh_token",
          data.refresh_token || refreshToken,
        );

        dispatch(
          submitGoogleToken(
            {
              google_token: {
                token: newAccessToken,
                refresh_token: data.refresh_token || refreshToken,
                token_uri: "https://oauth2.googleapis.com/token",
                client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
                client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
                scopes: data.scope ? data.scope.split(" ") : [],
                expiry: new Date(newExpiryTime).toISOString(),
              },
            },
            token,
          ),
        );

        return newAccessToken;
      } catch (error) {
        return null;
      }
    } else {
      return accessToken;
    }
  };

  const pickerCallback = async (data, accToken) => {
    if (
      data.action === window.google.picker.Action.PICKED ||
      data.action === window.google.picker.Action.CANCEL
    ) {
      detachScrollGuard(); // 👈 stop intercepting scroll once picker is closed
    }

    if (data.action === window.google.picker.Action.PICKED) {
      setShowUploadFileModal?.(true);
      setIsLoading?.(true);

      const files = await Promise.all(
        data.docs.map(async (doc) => {
          try {
            let downloadUrl;

            if (doc.mimeType === "application/vnd.google-apps.spreadsheet") {
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
              doc.mimeType = "application/xlsx";
              doc.name = `${doc.name}.xlsx`;
            } else if (
              doc.mimeType === "application/vnd.google-apps.document"
            ) {
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}/export?mimeType=application/vnd.openxmlformats-officedocument.wordprocessingml.document`;
              doc.mimeType = "application/docx";
              doc.name = `${doc.name}.docx`;
            } else {
              downloadUrl = `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`;
            }

            const response = await fetch(downloadUrl, {
              headers: new Headers({
                Authorization: `Bearer ${accToken}`,
              }),
            });

            if (!response.ok) {
              throw new Error(
                `Failed to fetch file: ${doc.name}, Status: ${response.status}`,
              );
            }

            const blob = await response.blob();

            if (blob.size === 0) {
              toast.error(`${doc.name} seems corrupted.`);
              return null;
            }

            return new File([blob], doc.name, {
              type: doc.mimeType || blob.type,
              lastModified: new Date(doc.lastEditedUtc).getTime(),
            });
          } catch (error) {
            console.error("Error fetching file:", error);
            setIsLoading?.(false);
            return null;
          }
        }),
      );

      const failedFiles = files.some((file) => file === null);

      if (failedFiles) {
        if (statusCode === 403) {
          toast.error("File/s too large to upload.");
        } else {
          toast.error("Failed to upload file/s.");
        }
        return;
      }

      setIsLoading?.(false);

      const filteredFiles = files.filter((file) => file !== null);
      if (filteredFiles.length > 0 && onFilesDownloaded) {
        onFilesDownloaded(filteredFiles);
      }
    }
  };

  const createPicker = (accToken) => {
    if (!accToken) return;

    const savedScrollY = window.scrollY; // 👈 capture scroll position before picker opens
    attachScrollGuard(savedScrollY); // 👈 start intercepting any scroll attempts

    const picker = new window.google.picker.PickerBuilder()
      .addView(window.google.picker.ViewId.DOCS)
      .setOAuthToken(accToken)
      .setDeveloperKey(process.env.REACT_APP_GOOGLEDRIVE_API_KEY)
      .setCallback((data) => pickerCallback(data, accToken))
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .build();

    picker.setVisible(true);
  };

  const handleOpenPicker = async (e) => {
    e.preventDefault();
    setShowUploadPop?.(false);

    const validToken = await handleTokenExpiration();

    if (!validToken) {
      loginWithGoogle();
    } else {
      createPicker(validToken);
    }
  };

  const onSuccess = async (tokenResponse) => {
    const response = await fetch("https://oauth2.googleapis.com/token", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({
        code: tokenResponse.code,
        client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
        client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
        redirect_uri: process.env.REACT_APP_SHARE_URL.replace(/\/$/, ""),
        grant_type: "authorization_code",
        access_type: "offline",
        include_granted_scopes: true,
      }),
    });

    const tokenData = await response.json();

    if (tokenData) {
      const creds = {
        google_token: {
          token: tokenData.access_token,
          refresh_token: tokenData.refresh_token || null,
          token_uri: "https://oauth2.googleapis.com/token",
          client_id: process.env.REACT_APP_GOOGLEDRIVE_CLIENTID,
          client_secret: process.env.REACT_APP_GOOGLEDRIVE_SECRET_KEY,
          scopes: (tokenData.scope || tokenResponse.scope || "")
            .split(" ")
            .filter(Boolean),
          expiry: new Date(
            Date.now() + tokenData.expires_in * 1000,
          ).toISOString(),
        },
      };

      dispatch(submitGoogleToken(creds, token));
      setAccessToken(tokenData?.access_token);
      createPicker(tokenData?.access_token);
    }
  };

  const onFailure = (error) => {
    console.error("Login failed:", error);
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess,
    onError: onFailure,
    scope: [
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/drive",
      "https://www.googleapis.com/auth/drive.metadata.readonly",
      "https://www.googleapis.com/auth/drive.metadata",
    ].join(" "),
    access_type: "offline",
    approval_prompt: "force",
    prompt: "consent",
    flow: "auth-code",
  });

  useEffect(() => {
    const apiScript = document.createElement("script");
    apiScript.src = "https://apis.google.com/js/api.js";
    apiScript.async = true;
    apiScript.onload = () => {
      window.gapi.load("picker", () => {
        setPickerInited(true);
      });
    };
    document.body.appendChild(apiScript);
  }, []);

  useEffect(() => {
    if (googleToken?.google_token !== null) {
      dispatch(getGoogleTokenAction(token));
    }
  }, [googleToken]);

  useEffect(() => {
    if (googleAccessToken?.googleToken) {
      setAccessToken(googleAccessToken?.googleToken?.google_token?.token);
    }
  }, [googleAccessToken]);

  // Safety net: detach scroll guard if component unmounts while picker is open
  useEffect(() => {
    return () => {
      detachScrollGuard();
      dispatch(resetsubmitgoogleToken());
    };
  }, []);

  return (
    <>
      {mobileUpload ? (
        <>
          <div
            className="border-none bg-transparent flex flex-col items-center gap-[11px]"
            onClick={(e) => handleOpenPicker(e)}
            disabled={!pickerInited}
            style={{ cursor: "pointer" }}
          >
            <span className="border rounded-[50%] flex justify-center items-center w-[4rem] h-[4rem]">
              <GoogleDriveIcon w="30" h="30" />
            </span>
            <div className="flex flex-col gap-1">
              <span
                className={
                  textClassName || "text-['#232323'] text-[0.9rem] font-normal"
                }
              >
                Upload from
              </span>
              <span
                className={
                  textClassName || "text-['#232323'] text-[0.9rem] font-normal"
                }
              >
                Google Drive
              </span>
            </div>
            {/* <input type="file" ref={fileInputRef} style={{ display: "none" }} />
            <div className="google-drive-image">
              <GoogleDriveIcon />
            </div>
            <p>Upload from Google Drive</p> */}
          </div>
        </>
      ) : (
        <div
          className="google-drive-popup-folder"
          onClick={(e) => handleOpenPicker(e)}
          disabled={!pickerInited}
          style={{ cursor: "pointer" }}
        >
          <input type="file" ref={fileInputRef} style={{ display: "none" }} />
          <div className="google-drive-image">
            {Icon ? <Icon /> : <GoogleDriveIcon/>}
          </div>
          <p className={textClassName}>
            {accessToken === undefined
              ? "Connect To Drive"
              : "Upload from Google Drive"}
          </p>
        </div>
      )}
    </>
  );
};

export default GoogleDriveUpload;
